import { Injectable, Inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApolloError } from '@apollo/client';
import { catchError, filter, map, Observable, Subject, Subscription, takeUntil, tap, throwError } from 'rxjs';

import {
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { PropertyPanelDevice } from '@shure/cloud/shared/models/devices';
import { UpdateResponse } from '@shure/cloud/shared/models/http';
import { OktaInterfaceService, monitorLoginState } from '@shure/cloud/shared/okta/data-access';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';
import { ApolloQueryErrorMapper } from '@shure/shared/angular/data-access/system-api/core';
import { DeviceBluetoothPower } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AssociateTagMutationResult,
	CloudDeviceApiService,
	DissociateTagMutationResult
} from '../api/cloud-device-api.service';
import { DevicePropertyPanelApiService } from '../api/device-property-panel-api.service';

import {
	PropertyPanelDeviceQueryGQL,
	PropertyPanelDeviceSubscriptionGQL,
	NodeChangeType,
	PropertyPanelDeviceFragment
} from './graphql/generated/cloud-sys-api';
import {
	CloudPropertyPanelRfChannelLinkedTransmitterSubscriptionGQL,
	PropertyPanelExternalDisplaysSubscriptionGQL
} from './graphql/generated/cloud-sys-api';
import { mapPropertyPanelDeviceFromSysApi } from './mappers/map-property-panel-device';
import { SysApiAudioChannelService } from './sys-api-audio-channel.service';

@Injectable({ providedIn: 'root' })
export class SysApiDevicePropertyPanelApiService extends DevicePropertyPanelApiService {
	private destroy$ = new Subject<void>();
	private readonly logger: ILogger;

	private readonly selectedDeviceSubscription = new SubscriptionManager({
		subscriptionType: 'property-panel',
		create: (config): Subscription => this.createDeviceSubscription(config),
		retryWaitMs: 5000,
		maxRetryAttempts: 1
	});

	private readonly linkedTransmitterSubscription = new SubscriptionManager({
		subscriptionType: 'property-panel',
		create: (config): Subscription => this.createLinkedTransmitterSubscription(config),
		retryWaitMs: 5000,
		maxRetryAttempts: 1
	});

	private readonly rfChannelSubscriptions = new SubscriptionManager({
		subscriptionType: 'property-panel',
		create: (config): Subscription => this.createRfChannelSubscription(config),
		retryWaitMs: 5000,
		maxRetryAttempts: 1
	});

	private readonly externalDisplaysSubscription = new SubscriptionManager({
		subscriptionType: 'property-panel',
		create: (config): Subscription => this.createExternalDisplaysSubscription(config),
		retryWaitMs: 5000,
		maxRetryAttempts: 1
	});

	private readonly iotGatewayFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-iot-gateway'), {
		initialValue: false
	});
	private readonly extendedWarrantyFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-extended-warranty'),
		{
			initialValue: false
		}
	);
	private readonly scheduledJobsFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-scheduled-jobs'),
		{
			initialValue: false
		}
	);
	private readonly scheduledJobsRebootFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-scheduled-jobs-reboot'),
		{
			initialValue: false
		}
	);
	private readonly iotRoomProxyFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-iot-room-proxy'),
		{
			initialValue: false
		}
	);
	private readonly conferencingPlatformFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-conferencing-platform-property'),
		{
			initialValue: false
		}
	);
	private readonly auracastFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-auracast'), {
		initialValue: false
	});
	private readonly useFirmwareUpdateV2 = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-use-firmware-update-v2'),
		{
			initialValue: false
		}
	);
	private readonly healthFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-device-health'), {
		initialValue: false
	});

	constructor(
		logger: ILogger,
		private readonly cloudDeviceService: CloudDeviceApiService,
		private readonly propertyPanelDeviceQueryGQL: PropertyPanelDeviceQueryGQL,
		private readonly propertyPanelDeviceSubscriptionGQL: PropertyPanelDeviceSubscriptionGQL,
		private readonly cloudPropPanelRfChannelGQL: CloudPropertyPanelRfChannelLinkedTransmitterSubscriptionGQL,
		private readonly propPanelExternalDisplaysSubscriptionGQL: PropertyPanelExternalDisplaysSubscriptionGQL,
		private readonly oktaService: OktaInterfaceService,
		private readonly audioChannelService: SysApiAudioChannelService,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService
	) {
		super();
		this.logger = logger.createScopedLogger('DaiDevicePropertyPanelService');

		monitorLoginState(this.oktaService, {
			onLogIn: this.initService,
			onLogOut: this.suspendService
		});
	}

	/**
	 * Get device by id
	 * @param deviceId
	 * @returns
	 */
	public getDevice$(deviceId: string): Observable<PropertyPanelDevice> {
		this.logger.trace('getDevice$()', 'propertyPanelDeviceQueryGQL', { deviceId });

		return this.propertyPanelDeviceQueryGQL
			.watch(
				{
					nodeId: deviceId,
					requestAuthenticationProperties: this.iotGatewayFeatureEnabled(),
					requestExtendedWarrantyProperties: this.extendedWarrantyFeatureEnabled(),
					requestConferencingPlatform: this.conferencingPlatformFeatureEnabled(),
					requestScheduledJobs: this.scheduledJobsFeatureEnabled(),
					requestScheduledRebootJobs: this.scheduledJobsRebootFeatureEnabled(),
					requestIotRoomProxy: this.iotRoomProxyFeatureEnabled(),
					requestAuracastProperties: this.auracastFeatureEnabled(),
					useFirmwareUpdateV2: this.useFirmwareUpdateV2(),
					requestDeviceHealth: this.healthFeatureEnabled()
				},
				{
					fetchPolicy: 'cache-and-network',
					errorPolicy: 'ignore',
					returnPartialData: true
				}
			)
			.valueChanges.pipe(
				filter((query) => query.data !== null && 'node' in query.data && 'isDeviceNode' in query.data.node),
				map((query) => {
					if ('isDeviceNode' in query.data.node) {
						return query.data.node;
					}
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((deviceFragment) => {
					// device prop panel subscription
					this.selectedDeviceSubscription.register([deviceFragment.id], true);

					// linked transmitter subscriptions
					const { rfChannelIds, linkedTransmitterIds } = this.getLinkedTransmitterIds(deviceFragment);
					this.rfChannelSubscriptions.register(rfChannelIds, true);
					this.linkedTransmitterSubscription.register(linkedTransmitterIds, true);

					// external displays subscriptions
					const externalDisplayIds = this.getExternalDisplayIds(deviceFragment);
					this.externalDisplaysSubscription.register(externalDisplayIds, true);
				}),
				map((deviceFragment) => {
					const audioChannelAuracastFragments = this.audioChannelService.handleAuracastChannels(
						deviceFragment.id,
						deviceFragment
					);
					return mapPropertyPanelDeviceFromSysApi(deviceFragment, audioChannelAuracastFragments);
				}),
				catchError((error) => {
					this.logger.error(
						'getDevice$()',
						'Error querying PropertyPanel device',
						JSON.stringify({
							deviceId,
							error
						})
					);
					return throwError(() => error);
				})
			);
	}

	/**
	 * Tell the service to "forget" about this device. Includes unsubscribing to
	 * the more detailed property subscriptions.
	 * @param nodeId
	 */
	public forgetDevice(nodeId: string): void {
		this.selectedDeviceSubscription.deregister(nodeId);
		this.rfChannelSubscriptions.deregisterAll();
		this.linkedTransmitterSubscription.deregisterAll();
		this.externalDisplaysSubscription.deregisterAll();
		this.audioChannelService.removeAudioChannelSubscriptions(nodeId);
	}

	/**
	 * Set mute for a device.
	 * @param deviceId
	 * @param mute
	 * @returns
	 */
	public setMute(deviceId: string, mute: boolean): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setMute()', 'Setting mute', { deviceId, mute });
		return this.cloudDeviceService.setMute(deviceId, mute);
	}

	/**
	 * Set device name.
	 * @param deviceId
	 * @param name
	 * @returns
	 */
	public setDeviceName(deviceId: string, name: string): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setDeviceName()', 'Setting device name', JSON.stringify({ deviceId, name }));
		return this.cloudDeviceService.setDeviceName(deviceId, name);
	}

	/**
	 * Set device identifying state.
	 * @param deviceId
	 * @param identify
	 * @returns
	 */
	public setIdentify(deviceId: string, identify: boolean): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setIdentify()', 'Setting identify', { deviceId, identify });
		return this.cloudDeviceService.setIdentify(deviceId, identify);
	}

	/**
	 * Reboot a device
	 * @param deviceId
	 * @returns
	 */
	public rebootDevice(deviceId: string): Observable<UpdateResponse<void, string>> {
		this.logger.trace('rebootDevice()', 'Rebooting device', { deviceId });
		return this.cloudDeviceService.rebootDevice$(deviceId);
	}

	public override associateTag(deviceIds: string[], tag: string): Observable<AssociateTagMutationResult> {
		return this.cloudDeviceService.associateTag(deviceIds, tag);
	}

	public override dissociateTag(deviceIds: string[], tag: string): Observable<DissociateTagMutationResult> {
		return this.cloudDeviceService.dissociateTag(deviceIds, tag);
	}

	/**
	 * Enable or disable IoT Room Proxy feature
	 * @param deviceId
	 * @param enabled
	 * @returns
	 */
	public override setIotRoomProxy(deviceId: string, enabled: boolean): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setIotRoomProxy()', 'Setting IoT Room Proxy feature', { deviceId, enabled });
		return this.cloudDeviceService.setIotRoomProxy(deviceId, enabled);
	}

	/**
	 * Enable or disable Auracast on an audio channel
	 */
	public override setAuracastEnabled(channelId: string, enabled: boolean): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setAuracastEnabled()', 'Setting Auracast enabled', { channelId, enabled });
		return this.cloudDeviceService.setAuracastEnabled(channelId, enabled);
	}

	/**
	 * Set the Auracast broadcast name on an audio channel
	 */
	public override setBroadcastName(
		channelId: string,
		broadcastName: string
	): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setBroadcastName()', 'Setting broadcast name', { channelId, broadcastName });
		return this.cloudDeviceService.setBroadcastName(channelId, broadcastName);
	}

	/**
	 * Enable Auracast broadcast code (encryption) on an audio channel
	 */
	public override enableBroadcastCode(
		channelId: string,
		passphrase: string
	): Observable<UpdateResponse<void, string>> {
		this.logger.trace('enableBroadcastCode()', 'Enabling broadcast code', { channelId });
		return this.cloudDeviceService.enableBroadcastCode(channelId, passphrase);
	}

	/**
	 * Disable Auracast broadcast code (encryption) on an audio channel
	 */
	public override disableBroadcastCode(channelId: string): Observable<UpdateResponse<void, string>> {
		this.logger.trace('disableBroadcastCode()', 'Disabling broadcast code', { channelId });
		return this.cloudDeviceService.disableBroadcastCode(channelId);
	}

	/**
	 * Set Bluetooth power level on a device
	 */
	public override setBluetoothPowerLevel(
		deviceId: string,
		level: DeviceBluetoothPower
	): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setBluetoothPowerLevel()', 'Setting Bluetooth power level', { deviceId, level });
		return this.cloudDeviceService.setBluetoothPowerLevel(deviceId, level);
	}

	private initService = (): void => {
		this.logger.information('initService', 'user logged in, initializating service');
		this.destroy$ = new Subject();
		this.audioChannelService.initialize();
	};

	private suspendService = (): void => {
		this.logger.information('suspendService', 'user logged out, suspending service');
		this.destroy$.next();
		this.destroy$.complete();
		this.selectedDeviceSubscription.deregisterAll();
		this.audioChannelService.suspend();
	};

	private createDeviceSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		const subscriptionTypes = [
			NodeChangeType.DeviceAudioChannelCount,
			NodeChangeType.DeviceAudioMute,
			NodeChangeType.DeviceAudioNetwork,
			NodeChangeType.DeviceAvailablePackages,
			NodeChangeType.DeviceBatteryLevel,
			NodeChangeType.DeviceControlNetwork,
			NodeChangeType.DeviceDanteAudioNetwork,
			NodeChangeType.DeviceExternalDisplays,
			NodeChangeType.DeviceHostedFirmware,
			NodeChangeType.DeviceIdentify,
			NodeChangeType.DeviceLicense,
			NodeChangeType.DeviceLicenseV2,
			NodeChangeType.DeviceLicenseV3,
			NodeChangeType.DeviceMicStatus,
			NodeChangeType.DeviceMicStatusV2,
			NodeChangeType.DeviceName,
			NodeChangeType.DeviceTags,
			NodeChangeType.DevicePairedDevices
		];
		if (this.iotGatewayFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceAuthentication);
		}

		if (this.extendedWarrantyFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceExtendedWarranty);
		}

		if (this.scheduledJobsFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceScheduledJobs);
		}

		if (this.iotRoomProxyFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceIotRoomProxy);
		}

		if (this.conferencingPlatformFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceConferencingPlatform);
		}

		if (!this.useFirmwareUpdateV2()) {
			subscriptionTypes.push(NodeChangeType.DeviceUpdateProgress);
			subscriptionTypes.push(NodeChangeType.DeviceHostedUpdateProgress);
		}

		if (this.healthFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceHealth);
		}

		return this.propertyPanelDeviceSubscriptionGQL
			.subscribe(
				{
					id,
					types: subscriptionTypes,
					requestAuthenticationProperties: this.iotGatewayFeatureEnabled(),
					requestExtendedWarrantyProperties: this.extendedWarrantyFeatureEnabled(),
					requestConferencingPlatform: this.conferencingPlatformFeatureEnabled(),
					requestScheduledJobs: this.scheduledJobsFeatureEnabled(),
					requestScheduledRebootJobs: this.scheduledJobsRebootFeatureEnabled(),
					requestIotRoomProxy: this.iotRoomProxyFeatureEnabled(),
					requestAuracastProperties: this.auracastFeatureEnabled(),
					useFirmwareUpdateV2: this.useFirmwareUpdateV2(),
					requestDeviceHealth: this.healthFeatureEnabled()
				},
				{ fetchPolicy: 'network-only', errorPolicy: 'ignore' }
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (change) => {
					this.logger.trace('propPanelDevice (selectedDevice)', 'Updated', { id, change });
				},
				complete: () => {
					this.logger.debug('propPanelDevice (selectedDevice)', 'Completed', { id });
				},
				error: (error: ApolloError) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'propPanelDevice (selectedDevice)',
						'Encountered error',
						JSON.stringify({ id, error })
					);
					retryCallback();
				}
			});
	}

	private createLinkedTransmitterSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		const subscriptionTypes = [
			NodeChangeType.DeviceAudioMute,
			NodeChangeType.DeviceBatteryLevel,
			NodeChangeType.DeviceIdentify,
			NodeChangeType.DeviceMicStatus,
			NodeChangeType.DeviceMicStatusV2,
			NodeChangeType.DeviceName,
			NodeChangeType.DeviceScheduledJobs,
			NodeChangeType.DevicePairedDevices
		];
		if (this.iotGatewayFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceAuthentication);
		}

		if (this.extendedWarrantyFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceExtendedWarranty);
		}

		if (this.scheduledJobsFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceScheduledJobs);
		}

		if (this.iotRoomProxyFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceIotRoomProxy);
		}

		if (this.conferencingPlatformFeatureEnabled()) {
			subscriptionTypes.push(NodeChangeType.DeviceConferencingPlatform);
		}

		return this.propertyPanelDeviceSubscriptionGQL
			.subscribe(
				{
					id,
					types: subscriptionTypes,
					requestAuthenticationProperties: this.iotGatewayFeatureEnabled(),
					requestExtendedWarrantyProperties: this.extendedWarrantyFeatureEnabled(),
					requestConferencingPlatform: this.conferencingPlatformFeatureEnabled(),
					requestScheduledJobs: this.scheduledJobsFeatureEnabled(),
					requestScheduledRebootJobs: this.scheduledJobsRebootFeatureEnabled(),
					requestIotRoomProxy: this.iotRoomProxyFeatureEnabled(),
					requestAuracastProperties: this.auracastFeatureEnabled(),
					useFirmwareUpdateV2: this.useFirmwareUpdateV2(),
					requestDeviceHealth: this.healthFeatureEnabled()
				},
				{ fetchPolicy: 'network-only', errorPolicy: 'ignore' }
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (change) => {
					this.logger.trace('propPanelDevice (linked xmitter)', 'Updated', { id, change });
				},
				complete: () => {
					this.logger.debug('propPanelDevice (linked xmitter)', 'Completed', { id });
				},
				error: (error) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'propPanelDevice (linked xmitter)',
						'Encountered retryable error',
						JSON.stringify({ id, error })
					);
					retryCallback();
				}
			});
	}

	private createRfChannelSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		const subscriptionTypes = [NodeChangeType.RfChannelLinkedTransmitter];

		return this.cloudPropPanelRfChannelGQL
			.subscribe(
				{
					rfChannelNodeId: id,
					rfChannelNodeChangeType: subscriptionTypes
				},
				{ fetchPolicy: 'network-only', errorPolicy: 'ignore' }
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (change) => {
					this.logger.trace('propPanelRfChannel', 'Received update', { id, change });
				},
				complete: () => {
					this.logger.debug('propPanelRfChannel', 'Completed', { id });
				},
				error: (error) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error('propPanelRfChannel', 'Encountered error', JSON.stringify({ id, error }));
					retryCallback();
				}
			});
	}

	private getLinkedTransmitterIds(fragmentData: PropertyPanelDeviceFragment): {
		rfChannelIds: string[];
		linkedTransmitterIds: string[];
	} {
		const rfChannelIds: string[] = [];
		const linkedTransmitterIds: string[] = [];

		const rfChannels = fragmentData.features.rfChannels?.rfChannels;
		if (rfChannels) {
			for (const rfChannel of rfChannels) {
				rfChannelIds.push(rfChannel.id);
				const linkage = rfChannel.features.linkedTransmitter?.linkage;
				if (linkage?.device?.id) {
					linkedTransmitterIds.push(linkage.device.id);
				}
			}
		}
		return { rfChannelIds, linkedTransmitterIds };
	}

	private createExternalDisplaysSubscription({ id, retryCallback }: SubscriptionManagerConfigCreate): Subscription {
		return this.propPanelExternalDisplaysSubscriptionGQL
			.subscribe(
				{
					nodeId: id
				},
				{ fetchPolicy: 'network-only', errorPolicy: 'ignore' }
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (change) => {
					this.logger.trace('createExternalDisplaysSubscription', 'Received update', { id, change });
				},
				complete: () => {
					this.logger.debug('createExternalDisplaysSubscription', 'Completed', { id });
				},
				error: (error) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'createExternalDisplaysSubscription',
						'Encountered error',
						JSON.stringify({ id, error })
					);
					retryCallback();
				}
			});
	}

	private getExternalDisplayIds(fragmentData: PropertyPanelDeviceFragment): string[] {
		return fragmentData.features.externalDisplays?.displays?.map((display) => display.id) ?? [];
	}
}
